/**
 * The website address — plan-018 DOM, design §30.6.1 (ADR 0059 decision 2; ED-27, the website-reader wall).
 *
 * ```
 * refusalOf(text):
 *   t = text.trim();                     t.length > 2048                        → too_long
 *   new URL(t) throws                    (also: a relative input)               → malformed
 *   protocol ∉ {http:, https:}                                                  → scheme
 *   username or password present                                                → credentials
 *   port ≠ ""                            (WHATWG drops :80 on http, :443 on https) → port
 *   hostname a dotted quad or "[…]"      (WHATWG already writes 0x7f.1 as 127.0.0.1) → ip_literal
 *   fewer than two labels, an empty label, a label over 63 characters or not [a-z0-9-] or starting or ending "-",
 *   a top-level label neither alphabetic nor "xn--…", or a reserved suffix (localhost local internal test invalid
 *   example onion home.arpa lan corp)                                           → not_public_host
 *   → null
 * stored form: the URL's href with the fragment removed
 * ```
 *
 * DNS is never consulted here: whether the name resolves to public addresses is the factory's question, asked at read
 * time with the address screen (AC-3). The api and the wizard call {@link websiteAddressRefusalOf} (one sentence per
 * reason in the portal); only the factory may call {@link websiteUrlOf}, which is the one function that turns a stored
 * answer into something fetchable — the `website-reader-wall` rule enforces that import.
 */
import { ValidationError } from "../error/taxonomy.js";
import { WEBSITE_ADDRESS_CHARACTER_LIMIT } from "./limit.js";
export const WEBSITE_ADDRESS_REFUSAL_REASON = [
    "malformed",
    "scheme",
    "port",
    "credentials",
    "ip_literal",
    "not_public_host",
    "too_long",
];
/** Suffixes that never name a public website (special-use and commonly private names). */
export const RESERVED_HOST_SUFFIX = [
    "localhost",
    "local",
    "internal",
    "test",
    "invalid",
    "example",
    "onion",
    "home.arpa",
    "lan",
    "corp",
];
const DOTTED_QUAD = /^\d{1,3}(\.\d{1,3}){3}$/;
const LABEL = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;
const TOP_LEVEL = /^([a-z]{2,63}|xn--[a-z0-9-]{1,59})$/;
function refusal(reason) {
    return Object.freeze({ code: "website_address_refused", reason });
}
function parsed(text) {
    const trimmed = text.trim();
    if (trimmed.length > WEBSITE_ADDRESS_CHARACTER_LIMIT)
        return refusal("too_long");
    let url;
    try {
        url = new URL(trimmed);
    }
    catch {
        return refusal("malformed");
    }
    if (url.protocol !== "http:" && url.protocol !== "https:")
        return refusal("scheme");
    if (url.username !== "" || url.password !== "")
        return refusal("credentials");
    if (url.port !== "")
        return refusal("port");
    const host = url.hostname;
    if (DOTTED_QUAD.test(host) || host.startsWith("["))
        return refusal("ip_literal");
    const label = host.split(".");
    const top = label[label.length - 1];
    if (label.length < 2 || top === undefined || !label.every((one) => LABEL.test(one)) || !TOP_LEVEL.test(top)) {
        return refusal("not_public_host");
    }
    if (RESERVED_HOST_SUFFIX.some((suffix) => host === suffix || host.endsWith(`.${suffix}`))) {
        return refusal("not_public_host");
    }
    return { url };
}
/** The refusal of a typed or stored address, or `null` when it is a public `http`/`https` address. Never a URL. */
export function websiteAddressRefusalOf(text) {
    const result = parsed(text);
    return "url" in result ? null : result;
}
/**
 * The form the wizard and the api store in `field_value` (the URL's `href` without its fragment). Throws
 * {@link WebsiteAddressRefusedError} (`website_address_refused` with its reason) for an address {@link websiteAddressRefusalOf} refuses.
 */
export function storedWebsiteAddressOf(text) {
    const result = parsed(text);
    if (!("url" in result))
        throw refusedError(result);
    result.url.hash = "";
    return result.url.href;
}
/**
 * The fetchable URL of a stored address — FACTORY ONLY (the website-reader wall). Re-checks the address, because a
 * stored value outlives the rules it was checked under, and throws {@link WebsiteAddressRefusedError} rather than return a URL for one
 * it refuses.
 */
export function websiteUrlOf(stored) {
    const result = parsed(stored);
    if (!("url" in result))
        throw refusedError(result);
    result.url.hash = "";
    return result.url;
}
/** The thrown form of a refusal: a `400 website_address_refused` carrying its reason (the api's `detail.reason`). */
export class WebsiteAddressRefusedError extends ValidationError {
    reason;
    constructor(reason) {
        super({ code: "website_address_refused", message: `The website address was refused (${reason}).` });
        this.reason = reason;
    }
}
function refusedError(result) {
    return new WebsiteAddressRefusedError(result.reason);
}
//# sourceMappingURL=website-address.js.map